import "./Header.scss";
import React, { useEffect, useState } from "react";
import useCurrentPage from "../../hooks/useCurrentPage";
import { Dropdown } from "react-bootstrap";
import { notificationicon } from "../../assets/images/icons/icons";
import headerprofileicon from "../../assets/images/icons/header-profile.svg";
import useAuth from "../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import MainServices from "../../services/MainServices";
import SimpleBar from "simplebar-react";
import Close from "../../assets/images/icons/cross.svg";
import useConfig from "../../hooks/useConfig";
import * as Storage from "../../store/LocalStorage";
import { Tooltip } from "react-tooltip";
import menuIcon from "../../assets/images/icons/menuIcon.svg";
import useTheme from "../../hooks/useTheme";
import { THEME_STORAGE_KEY } from "../../context/Theme";

const formatTimeAgo = (timestamp) => {
  const diffMs = new Date() - new Date(timestamp);
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} min ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hr ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day(s) ago`;
};

export default function Header({ setSidebarToggle }) {
  const { currentPage } = useCurrentPage();
  const [isNotificationsOpen, setNotificationOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [clientLogo, setClientLogo] = useState(null);
  const config = useConfig();
  const { user, updateUserContext } = useAuth();
  const navigate = useNavigate();
  const { getEventWorkflowReview, getClientSettings } = MainServices();
  const [loginTime, setLoginTime] = useState(null);
  const { theme, themeToggle } = useTheme();
  const nextTheme = theme === "dark" ? "light" : "dark";

  const toggleSideBar = () => {
    setSidebarToggle((oldValue) => !oldValue);
  };

  useEffect(() => {
    setLoginTime(localStorage.getItem("login"));

    const syncStorage = (event) => {
      if (event.key === "logout") {
        navigate("/login");
        window.location.reload();
      }

      if (event.key === "login") {
        const newLogin = localStorage.getItem("login");
        if (newLogin && newLogin !== loginTime) {
          window.location.reload();
        }
      }
    };

    window.addEventListener("storage", syncStorage);
    return () => window.removeEventListener("storage", syncStorage);
  }, [navigate, loginTime]);

  const activePlantId =
    user?.otherPlants?.some((p) => p?.publicId === user?.selectedPlant)
      ? user.selectedPlant
      : user?.otherPlants?.[0]?.publicId || "";

  useEffect(() => {
    const plants = user?.otherPlants || [];
    if (!plants.length) return;

    const validPlantId = plants.some((p) => p?.publicId === user?.selectedPlant)
      ? user.selectedPlant
      : plants[0]?.publicId;

    if (!validPlantId || user?.selectedPlant === validPlantId) return;

    const session = Storage.get(config.sessionKey);
    if (!session) return;

    const parsed = JSON.parse(session);
    parsed.selectedPlant = validPlantId;
    updateUserContext(parsed);
    Storage.set(config?.sessionKey, JSON.stringify(parsed));
  }, [user?.selectedPlant, user?.otherPlants, config?.sessionKey, updateUserContext]);

  const onPlantChange = (e) => {
    if (!e.target.value) return;

    let session = Storage.get(config.sessionKey);
    if (session) {
      session = JSON.parse(session);
      session.selectedPlant = e.target.value;
      updateUserContext(session);
      Storage.set(config?.sessionKey, JSON.stringify(session));
      window.location.reload();
    }
  };

  const onLogout = () => {
    const selectedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    localStorage.clear();
    if (selectedTheme) {
      localStorage.setItem(THEME_STORAGE_KEY, selectedTheme);
    }
    localStorage.setItem("logout", Date.now());
    navigate("/login");
    window.location.reload();
  };

  const onNotification = async () => {
    const opening = !isNotificationsOpen;
    setNotificationOpen(opening);

    if (opening) {
      try {
        const details = {
          type: "my-list",
          pageNumber: 1,
          pageSize: 10,
          sorting: [{ key: "createdAt", direction: "DESC" }],
        };

        const response = await getEventWorkflowReview(details);

        if (response?.success) {
          const workflows = response.data || [];
          const flatList = workflows.flatMap((workflow) =>
            (workflow.data || []).map((item) => {
              const docType = item.moduleData?.docType;
              let displayType = item.workflowType?.replace(/_/g, " ") || "Task";
              if (docType === "GTP") {
                displayType = "GTP";
              }

              return {
                title: displayType,
                description: item.moduleData?.name || "Pending approval",
                time: formatTimeAgo(item.createdAt),
                refId: item.publicId,
                workflowConcurrencystamp: item.concurrencyStamp,
                publicId: item.workflowAssign?.typeId,
                workflowName: item.workflowName,
                rawType: item.workflowType,
                docType: item.moduleData?.docType,
              };
            })
          );
          setNotifications(flatList);
        } else {
          setNotifications([]);
        }
      } catch (err) {
        console.error("Error fetching notifications:", err);
        setNotifications([]);
      }
    }
  };

  const FALLBACK_ROUTE = "/my-tasks";

  const handleNotificationClick = (notification) => {
    setNotificationOpen(false);
    navigate(FALLBACK_ROUTE);
  };

  useEffect(() => {
    const cached = Storage.get("client_logo");
    if (cached) {
      setClientLogo(cached);
    } else {
      getClientLogo();
    }
  }, []);

  const getClientLogo = async () => {
    try {
      const resData = await getClientSettings();

      if (resData?.success) {
        const content = resData?.data?.content ? JSON.parse(resData.data.content) : {};
        const logo = content?.logo ? content.logo : "";

        if (logo) {
          setClientLogo(logo);
        }
      }
    } catch (res) {
      console.log(res);

      if (res.message) {
        console.log("An error occurred in getting client logo");
      }
    }
  };

  return (
    <div className="header-main header-main--sidebar-mini">
      <div className="nk-header is-light">
        <div className="container-fluid">
          <div className="nk-header-wrap">
            <div className="d-flex justify-content-between align-items-center pe-3">
              <div className="nk-menu-trigger d-xl-none ms-n1">
                <div
                  onClick={toggleSideBar}
                  className="nk-nav-toggle nk-quick-nav-icon"
                  data-target="sidebarMenu"
                  style={{ cursor: "pointer" }}
                  data-tooltip-id="ToggleTooltip"
                  data-tooltip-content="View Menu"
                >
                  <img src={menuIcon} alt="Menu icon" width={20} height={20} />
                  <Tooltip id="ToggleTooltip" place="top" effect="solid" style={{ zIndex: 99999 }} />
                </div>
              </div>
              <div className="header-page-title">
                {currentPage?.subtitle ? (
                  <>
                    <span className="page-breadcrumb-root">{currentPage?.title}</span>
                    <span className="page-breadcrumb-sep" aria-hidden="true">›</span>
                    <span className="page-breadcrumb-current">{currentPage?.subtitle}</span>
                  </>
                ) : (
                  <span className="page-breadcrumb-current">{currentPage?.title}</span>
                )}
              </div>
              <div className="header-client-logo" aria-hidden={!clientLogo}>
                {clientLogo ? (
                  <img src={clientLogo} alt="Client logo" />
                ) : null}
              </div>
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className={`theme-toggle-button theme-toggle-button--${theme}`}
                  onClick={themeToggle}
                  aria-label={`Switch to ${nextTheme} mode`}
                  title={`Switch to ${nextTheme} mode`}
                >
                  <span className="theme-toggle-track" aria-hidden="true">
                    <span className={`theme-toggle-icon theme-toggle-icon--sun ${theme === "light" ? "is-active" : ""}`}>
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="4.25" stroke="currentColor" strokeWidth="2" />
                        <path d="M12 2.75V5.25" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <path d="M12 18.75V21.25" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <path d="M21.25 12H18.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <path d="M5.25 12H2.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <path d="M18.54 5.46L16.77 7.23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <path d="M7.23 16.77L5.46 18.54" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <path d="M18.54 18.54L16.77 16.77" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <path d="M7.23 7.23L5.46 5.46" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </span>
                    <span className="theme-toggle-thumb" />
                    <span className={`theme-toggle-icon theme-toggle-icon--moon ${theme === "dark" ? "is-active" : ""}`}>
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M20.2 14.2C19.28 17.86 15.98 20.5 12 20.5C7.31 20.5 3.5 16.69 3.5 12C3.5 8.02 6.14 4.72 9.8 3.8C8.96 4.95 8.46 6.36 8.46 7.88C8.46 11.75 11.59 14.88 15.46 14.88C16.98 14.88 18.39 14.38 19.54 13.54C19.8 13.35 20.29 13.84 20.2 14.2Z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </span>
                </button>
                {user?.otherPlants && user?.otherPlants.length > 0 && (
                  <select
                    className="sop-custom-form-select"
                    name="selectedPlant"
                    data-placeholder="Select Plant"
                    onChange={onPlantChange}
                    value={activePlantId}
                  >
                    {user?.otherPlants.map((each) => (
                      <option
                        value={each?.publicId}
                        key={each?.publicId}
                        style={{ textTransform: "capitalize" }}
                      >
                        {each.plantName}
                      </option>
                    ))}
                  </select>
                )}
                <div
                  className="notification-icon-main"
                  onClick={onNotification}
                  style={{ cursor: "pointer" }}
                >
                  <div className="notification-icon-graphic">{notificationicon}</div>
                </div>
                <Dropdown className="dropdown-main">
                  <Dropdown.Toggle
                    variant=""
                    id="dropdown-basic"
                    className="dropdown-content-main"
                  >
                    <div className="d-flex align-items-center">
                      <div className="header-profile-icon-main">
                        <img src={headerprofileicon} alt="profile icon" />
                      </div>
                      <div className="ms-2" style={{ textAlign: "left" }}>
                        <p className="m-0">{user?.name}</p>
                        <p className="m-0">{user?.email}</p>
                      </div>
                    </div>
                  </Dropdown.Toggle>

                  <Dropdown.Menu>
                    <Dropdown.Item
                      onClick={() =>
                        navigate({
                          pathname: "/profile",
                          search: `?user_id=${user.user_id}`,
                        })
                      }
                    >
                      Profile
                    </Dropdown.Item>
                    <Dropdown.Item onClick={onLogout}>Sign Out</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
          </div>
        </div>
        <div
          className={`toggle-slide-right ${
            isNotificationsOpen ? "slide-right-open" : "slide-right-close"
          }`}
          id="mobile-menu"
          style={{
            overflowY: "scroll",
            position: "fixed",
            width: "450px",
          }}
        >
          <div className="notifications">
            <div className="notifications-head">
              <h6>Notifications</h6>
              <div
                onClick={() => onNotification()}
                className="notification-close mx-3"
              >
                <img src={Close} alt="list-icon" />
              </div>
            </div>
            <SimpleBar className="notifications-content">
              {notifications.length > 0 ? (
                notifications.map((n, idx) => (
                  <div
                    className="each-notification"
                    key={idx}
                    onClick={() => handleNotificationClick(n)}
                    style={{ cursor: "pointer" }}
                  >
                    <p className="notification-title">{n.title}</p>
                    <p className="notification-info">{n.description}</p>
                    <p className="notification-time">{n.time}</p>
                    <hr />
                  </div>
                ))
              ) : (
                <p style={{ padding: "1rem" }}>No notifications</p>
              )}
            </SimpleBar>
          </div>
        </div>
      </div>
    </div>
  );
}
